import './Tree.css';

import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useCallback, useMemo, useRef, useState } from 'react';

import { CloseSVG } from '../../assets/icons';
import { IconButton } from '../buttons/IconButton';
import { MoreActions } from './MoreActions';
import { TextInputDebounced } from '../form/text-input-debounced/TextInputDebounced';
import { DropPosition, TreeItem } from './TreeItem';

interface Action {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
}

export interface ActionItem<T> {
    label: string;
    onClick: (value: T) => void;
    icon?: React.ReactNode;
}

export interface ItemDefinition<T> {
    render: (v: T) => React.ReactNode;
    icon?: (v: T) => React.ReactNode;
    actions?: (v: T) => ActionItem<T>[];
    children?: (v: T) => T[] | undefined;
    onItemClicked?: (v: T) => void;
    getId: (v: T) => string | number;
    onItemMoved?: (draggedItem: T, targetItem: T) => void;
    onItemReordered?: (draggedItem: T, targetItem: T, position: Exclude<DropPosition, 'inside'>) => void;
    canItemMoveTo?: (draggedItem: T, targetItem: T) => boolean;
}

interface Header {
    label: string;
    actions?: Action[];
}

interface ITreeProps<T> {
    items: T[];
    itemDefinition: ItemDefinition<T>;
    header: Header;
    isItemActive: (item: T) => boolean;
    searchValue?: string;
    filter?: (value: string) => void;
    /** Items start expanded (default: collapsed, except the path to the active item). */
    defaultExpanded?: boolean;
}

export function Tree<T>(props: ITreeProps<T>) {
    const [selected, setSelected] = useState<T>();
    const [activeItem, setActiveItem] = useState<T | null>(null);
    const activeDropPositionRef = useRef<DropPosition>('inside');

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    /****************************
    * DATA MANIPULATION EFFECTS
    *****************************/



    /****************************
    * USER INTERACTION
    *****************************/

    const handleMainActionClicked = useCallback((event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | undefined, action: Action) => {
        event?.stopPropagation();
        action.onClick()
    }, [])

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveItem((event.active.data.current?.item as T) ?? null);
        activeDropPositionRef.current = 'inside';
    }, []);

    const handleDropPositionChange = useCallback((position: DropPosition) => {
        activeDropPositionRef.current = position;
    }, []);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        setActiveItem(null);
        if (!over) return;

        const draggedItem = active.data.current?.item as T;
        const targetItem = over.data.current?.item as T;
        if (!draggedItem || !targetItem) return;
        if (props.itemDefinition.getId(draggedItem) === props.itemDefinition.getId(targetItem)) return;

        const position = activeDropPositionRef.current;
        if (position === 'inside') {
            if (props.itemDefinition.canItemMoveTo && !props.itemDefinition.canItemMoveTo(draggedItem, targetItem)) return;
            props.itemDefinition.onItemMoved?.(draggedItem, targetItem);
        } else {
            props.itemDefinition.onItemReordered?.(draggedItem, targetItem, position);
        }
    }, [props.itemDefinition]);


    /****************************
    * CSS && HTML
    *****************************/

    const mainAction = useMemo(() => {
        const [action] = props.header.actions || [];

        if (action === undefined) {
            return <div className={"tree-header-main-action"}></div>
        }

        return (
            <IconButton icon={action.icon} className={"tree-header-main-action"} onClick={(e) => handleMainActionClicked(e, action)} />
        )
    }, [props.header.actions])

    return <div className='tree'>
        <div className='tree-header'>
            <div className='tree-header-label'>{props.header.label}</div>
            <div className='tree-header-actions'>
                {mainAction}
                <MoreActions
                    actions={props.header.actions ? props.header.actions.slice(1) : []}
                    item={undefined}
                    highlighted={false}
                    className='header'
                />
            </div>
        </div>

        <div className="filter">
            <TextInputDebounced
                value={props.searchValue}
                placeholder="Insert the page name"
                onChange={(val) => props.filter && props.filter(val)}
                icon={<CloseSVG />}
            />
        </div>

        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className='tree-body'>
                {props.items.map((item) =>
                    <TreeItem
                        key={props.itemDefinition.getId(item)}
                        item={item}
                        isItemActive={props.isItemActive}
                        itemDefinition={props.itemDefinition}
                        onSelectedItemChanged={setSelected}
                        selectedItem={selected}
                        onDropPositionChange={handleDropPositionChange}
                        activeItem={activeItem}
                        defaultExpanded={props.defaultExpanded}
                    />)}
            </div>
            <DragOverlay>
                {activeItem ? (
                    <div className="tree-item-drag-overlay">
                        <div className="tree-item-content">
                            <div className="tree-label">{props.itemDefinition.render(activeItem)}</div>
                        </div>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    </div>
}
